import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Checkbox,
  InputAdornment,
  Slider,
  Divider,
  Button,
  IconButton,
  Collapse,
  Tooltip,
  LinearProgress,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, HelpOutline as HelpIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import type {
  PayoutSchemeConfig,
  CustomPayoutCategory,
  NeighborConfig,
  NeighborDirection,
  BoxNeighborConfig,
  CellMode,
} from '../types';
import {
  ALL_NEIGHBOR_DIRECTIONS,
  CARDINAL_DIRECTIONS,
  DIAGONAL_DIRECTIONS,
  BOX_NEIGHBOR_SIZES,
  getBoxNeighborCount,
} from '../types';

// Simple UUID generator for custom categories with fallback for older browsers
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback UUID v4 generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export interface PayoutConfigEditorProps {
  payoutConfig: PayoutSchemeConfig;
  onPayoutConfigChange: (config: PayoutSchemeConfig) => void;
  pricePerSquare: number;
  onPricePerSquareChange?: (price: number) => void; // When provided, shows editable price field
  profitPercent: number;
  onProfitPercentChange: (percent: number) => void;
  quarters: number;
  totalCells?: number; // Number of cells (100 for standard, 50 for paired, 25 for quad)
  cellMode?: CellMode; // Cell mode - box neighbors only available for 'standard'
  readOnly?: boolean;
  compact?: boolean;
  hideProfit?: boolean; // Hide profit display in read-only mode (for Rules tab)
}

const MAX_PROFIT_PERCENT = 50;

// Custom hook for number input with better typing UX
const useNumberInput = (
  value: number,
  onChange: (value: number) => void,
  min: number,
  max: number
) => {
  const [inputValue, setInputValue] = useState(value.toString());

  // Sync external value changes
  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Allow empty string and valid number patterns while typing
    if (newValue === '' || newValue === '-' || /^-?\d*\.?\d*$/.test(newValue)) {
      setInputValue(newValue);
    }
  };

  const handleBlur = () => {
    const parsed = parseFloat(inputValue);
    if (isNaN(parsed) || inputValue === '') {
      // Reset to current value if invalid
      setInputValue(value.toString());
    } else {
      // Clamp and update
      const clamped = Math.max(min, Math.min(max, parsed));
      setInputValue(clamped.toString());
      if (clamped !== value) {
        onChange(clamped);
      }
    }
  };

  return { inputValue, handleChange, handleBlur };
};

// Quarter payout input component using the custom hook - now for DOLLAR amounts
interface QuarterPayoutInputProps {
  quarterKey: string;
  label: string;
  dollarAmount: number;
  wrongWayAmount: number;
  plusNAmount: number;
  plusNOffset: number;
  payoutPot: number;
  showWrongWay: boolean;
  showPlusN: boolean;
  onUpdate: (key: string, value: number) => void;
  onUpdateWrongWay: (key: string, value: number) => void;
  onUpdatePlusN: (key: string, value: number) => void;
}

const QuarterPayoutInput: React.FC<QuarterPayoutInputProps> = ({
  quarterKey,
  label,
  dollarAmount,
  wrongWayAmount,
  plusNAmount,
  plusNOffset,
  payoutPot,
  showWrongWay,
  showPlusN,
  onUpdate,
  onUpdateWrongWay,
  onUpdatePlusN,
}) => {
  const input = useNumberInput(
    dollarAmount,
    (value) => onUpdate(quarterKey, value),
    0,
    payoutPot
  );

  return (
    <Grid size={{ xs: 6, sm: 3 }}>
      <TextField
        fullWidth
        label={label}
        value={input.inputValue}
        onChange={input.handleChange}
        onBlur={input.handleBlur}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        size="small"
      />
      {showWrongWay && (
        <TextField
          fullWidth
          label="Wrong Way"
          type="number"
          value={wrongWayAmount || ''}
          onChange={(e) => onUpdateWrongWay(quarterKey, parseFloat(e.target.value) || 0)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          size="small"
          sx={{ mt: 1 }}
          placeholder="0"
        />
      )}
      {showPlusN && (
        <TextField
          fullWidth
          label={`+${plusNOffset} Rule`}
          type="number"
          value={plusNAmount || ''}
          onChange={(e) => onUpdatePlusN(quarterKey, parseFloat(e.target.value) || 0)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          size="small"
          sx={{ mt: 1 }}
          placeholder="0"
        />
      )}
    </Grid>
  );
};

// Get quarter labels based on sport and quarters
const getQuarterLabels = (quarters: number): { key: string; label: string }[] => {
  if (quarters === 2) {
    return [
      { key: '1H', label: '1st Half' },
      { key: '2H', label: '2nd Half / Final' },
    ];
  }
  if (quarters === 3) {
    return [
      { key: 'P1', label: '1st Period' },
      { key: 'P2', label: '2nd Period' },
      { key: 'P3', label: '3rd Period / Final' },
    ];
  }
  return [
    { key: 'Q1', label: 'Q1' },
    { key: 'Q2', label: 'Q2 / Halftime' },
    { key: 'Q3', label: 'Q3' },
    { key: 'Q4', label: 'Q4 / Final' },
  ];
};

export const PayoutConfigEditor: React.FC<PayoutConfigEditorProps> = ({
  payoutConfig,
  onPayoutConfigChange,
  pricePerSquare,
  onPricePerSquareChange,
  profitPercent,
  onProfitPercentChange,
  quarters,
  totalCells = 100,
  cellMode = 'standard',
  readOnly = false,
  compact = false,
  hideProfit = false,
}) => {
  const quarterLabels = getQuarterLabels(quarters);
  const quarterKeys = quarterLabels.map(q => q.key);

  // Calculate pot values (use totalCells for cell-grouped modes)
  const totalPot = pricePerSquare * totalCells;
  const profitAmount = totalPot * (profitPercent / 100);
  const payoutPot = totalPot - profitAmount;

  // Calculate current total allocated dollars (period + wrong-way + +N + custom categories)
  const periodAllocatedDollars = quarterKeys.reduce(
    (sum, key) => sum + (payoutConfig.quarterPayouts?.[key] || 0),
    0
  );
  const quarterWrongWayAllocatedDollars = quarterKeys.reduce(
    (sum, key) => sum + (payoutConfig.quarterWrongWayPayouts?.[key] || 0),
    0
  );
  const quarterPlusNAllocatedDollars = quarterKeys.reduce(
    (sum, key) => sum + (payoutConfig.quarterPlusNPayouts?.[key] || 0),
    0
  );
  const quarterPlusNWrongWayAllocatedDollars = quarterKeys.reduce(
    (sum, key) => sum + (payoutConfig.quarterPlusNWrongWayPayouts?.[key] || 0),
    0
  );
  // Custom categories: if wrongWayEnabled, payout is doubled
  const customAllocatedDollars = (payoutConfig.customCategories || []).reduce(
    (sum, cat) => {
      const baseAmount = cat.payoutAmount || 0;
      return sum + (cat.wrongWayEnabled ? baseAmount * 2 : baseAmount);
    },
    0
  );

  // Neighbor payouts calculation
  const neighborConfig = payoutConfig.neighborPayouts;
  const neighborDirections = neighborConfig?.directions || [];
  const neighborAllocatedDollars = (() => {
    if (!neighborConfig?.enabled) return 0;
    const directionCount = neighborDirections.length || 0;
    if (neighborConfig.usePerQuarterAmounts && neighborConfig.quarterAmounts) {
      return quarterKeys.reduce(
        (sum, key) => sum + (neighborConfig.quarterAmounts?.[key] || 0) * directionCount,
        0
      );
    }
    // Global amount per direction * number of directions * number of quarters
    return (neighborConfig.globalAmount || 0) * directionCount * quarterKeys.length;
  })();

  // Bad numbers payouts calculation
  // Count is now based on explicit badCombinations (or legacy badDigits)
  const badNumbersConfig = payoutConfig.badNumbersConfig;
  const badNumbersAllocatedDollars = (() => {
    if (!badNumbersConfig?.enabled) return 0;
    // Use badCombinations if available, otherwise fall back to legacy badDigits calculation
    if (badNumbersConfig.badCombinations && badNumbersConfig.badCombinations.length > 0) {
      return badNumbersConfig.badCombinations.length * (badNumbersConfig.payoutAmount || 0);
    }
    // Legacy: calculate from badDigits
    const badDigitCount = badNumbersConfig.badDigits?.length || 0;
    const badSquareCount = badDigitCount * badDigitCount;
    return badSquareCount * (badNumbersConfig.payoutAmount || 0);
  })();

  // Box neighbor payouts calculation
  const boxNeighborAllocatedDollars = (() => {
    const boxConfig = payoutConfig.boxNeighborPayouts;
    if (!boxConfig?.enabled) return 0;
    const neighborCount = getBoxNeighborCount(boxConfig.boxSize);
    if (boxConfig.usePerQuarterAmounts && boxConfig.quarterAmounts) {
      return quarterKeys.reduce(
        (sum, key) => sum + (boxConfig.quarterAmounts?.[key] || 0) * neighborCount,
        0
      );
    }
    // Global amount per neighbor * number of neighbors * number of quarters
    return (boxConfig.globalAmount || 0) * neighborCount * quarterKeys.length;
  })();

  const allocatedDollars = periodAllocatedDollars + quarterWrongWayAllocatedDollars + quarterPlusNAllocatedDollars + quarterPlusNWrongWayAllocatedDollars + customAllocatedDollars + neighborAllocatedDollars + badNumbersAllocatedDollars + boxNeighborAllocatedDollars;
  const remainingDollars = payoutPot - allocatedDollars;

  // State for right-way toggle - default on if rightWayEnabled is true or any quarter payouts exist
  const hasRightWayPayouts = quarterKeys.some(key => (payoutConfig.quarterPayouts?.[key] || 0) > 0);
  const [rightWayEnabled, setRightWayEnabled] = useState(payoutConfig.rightWayEnabled ?? hasRightWayPayouts ?? true);

  // State for wrong-way toggle - default on if any wrong-way payouts exist or wrongWayEnabled is true
  const hasWrongWayPayouts = quarterKeys.some(key => (payoutConfig.quarterWrongWayPayouts?.[key] || 0) > 0);
  const [wrongWayEnabled, setWrongWayEnabled] = useState(payoutConfig.wrongWayEnabled ?? hasWrongWayPayouts);

  // State for +N toggle - default on if any +N payouts exist or plusFiveEnabled is true
  const hasPlusNPayouts = quarterKeys.some(key => (payoutConfig.quarterPlusNPayouts?.[key] || 0) > 0);
  const [plusNEnabled, setPlusNEnabled] = useState(payoutConfig.plusFiveEnabled || hasPlusNPayouts);

  // State for +N applies to RW/WW
  const [plusNRightWayEnabled, setPlusNRightWayEnabled] = useState(payoutConfig.plusNRightWayEnabled ?? true);
  const [plusNWrongWayEnabled, setPlusNWrongWayEnabled] = useState(payoutConfig.plusNWrongWayEnabled ?? true);

  // Sync toggle states when payoutConfig prop changes (e.g., when template loads from backend)
  useEffect(() => {
    const hasRW = quarterKeys.some(key => (payoutConfig.quarterPayouts?.[key] || 0) > 0);
    setRightWayEnabled(payoutConfig.rightWayEnabled ?? hasRW ?? true);

    const hasWW = quarterKeys.some(key => (payoutConfig.quarterWrongWayPayouts?.[key] || 0) > 0);
    setWrongWayEnabled(payoutConfig.wrongWayEnabled ?? hasWW);

    const hasPN = quarterKeys.some(key => (payoutConfig.quarterPlusNPayouts?.[key] || 0) > 0);
    setPlusNEnabled(payoutConfig.plusFiveEnabled || hasPN);

    setPlusNRightWayEnabled(payoutConfig.plusNRightWayEnabled ?? true);
    setPlusNWrongWayEnabled(payoutConfig.plusNWrongWayEnabled ?? true);
  }, [
    payoutConfig.rightWayEnabled,
    payoutConfig.wrongWayEnabled,
    payoutConfig.plusFiveEnabled,
    payoutConfig.plusNRightWayEnabled,
    payoutConfig.plusNWrongWayEnabled,
    payoutConfig.quarterPayouts,
    payoutConfig.quarterWrongWayPayouts,
    payoutConfig.quarterPlusNPayouts,
    quarterKeys,
  ]);

  // State for neighbor payouts
  const [neighborEnabled, setNeighborEnabled] = useState(payoutConfig.neighborPayouts?.enabled ?? false);
  const [neighborUsePerQuarter, setNeighborUsePerQuarter] = useState(payoutConfig.neighborPayouts?.usePerQuarterAmounts ?? false);

  // Sync neighbor state when payoutConfig prop changes (e.g., when template loads)
  useEffect(() => {
    setNeighborEnabled(payoutConfig.neighborPayouts?.enabled ?? false);
    setNeighborUsePerQuarter(payoutConfig.neighborPayouts?.usePerQuarterAmounts ?? false);
  }, [payoutConfig.neighborPayouts?.enabled, payoutConfig.neighborPayouts?.usePerQuarterAmounts]);

  // State for box neighbor payouts
  const [boxNeighborEnabled, setBoxNeighborEnabled] = useState(payoutConfig.boxNeighborPayouts?.enabled ?? false);
  const [boxNeighborUsePerQuarter, setBoxNeighborUsePerQuarter] = useState(payoutConfig.boxNeighborPayouts?.usePerQuarterAmounts ?? false);

  // Sync box neighbor state when payoutConfig prop changes
  useEffect(() => {
    setBoxNeighborEnabled(payoutConfig.boxNeighborPayouts?.enabled ?? false);
    setBoxNeighborUsePerQuarter(payoutConfig.boxNeighborPayouts?.usePerQuarterAmounts ?? false);
  }, [payoutConfig.boxNeighborPayouts?.enabled, payoutConfig.boxNeighborPayouts?.usePerQuarterAmounts]);

  // State for bad numbers payouts
  const [badNumbersEnabled, setBadNumbersEnabled] = useState(payoutConfig.badNumbersConfig?.enabled ?? false);
  const [selectedBadCombos, setSelectedBadCombos] = useState<string[]>(() => {
    // Initialize from badCombinations or generate from legacy badDigits
    const config = payoutConfig.badNumbersConfig;
    if (config?.badCombinations && config.badCombinations.length > 0) {
      return config.badCombinations;
    }
    if (config?.badDigits && config.badDigits.length > 0) {
      // Generate combinations from legacy digits
      const combos: string[] = [];
      config.badDigits.forEach(r => config.badDigits!.forEach(c => combos.push(`${r}-${c}`)));
      return combos;
    }
    return [];
  });
  const [badNumbersPayoutAmount, setBadNumbersPayoutAmount] = useState(payoutConfig.badNumbersConfig?.payoutAmount ?? 0);

  // Sync bad numbers state when payoutConfig prop changes
  useEffect(() => {
    setBadNumbersEnabled(payoutConfig.badNumbersConfig?.enabled ?? false);
    const config = payoutConfig.badNumbersConfig;
    if (config?.badCombinations && config.badCombinations.length > 0) {
      setSelectedBadCombos(config.badCombinations);
    } else if (config?.badDigits && config.badDigits.length > 0) {
      const combos: string[] = [];
      config.badDigits.forEach(r => config.badDigits!.forEach(c => combos.push(`${r}-${c}`)));
      setSelectedBadCombos(combos);
    } else {
      setSelectedBadCombos([]);
    }
    setBadNumbersPayoutAmount(payoutConfig.badNumbersConfig?.payoutAmount ?? 0);
  }, [payoutConfig.badNumbersConfig]);

  // Update bad numbers config helper
  const updateBadNumbersConfig = (updates: Partial<typeof badNumbersConfig>) => {
    const currentConfig = payoutConfig.badNumbersConfig ?? { enabled: false, badCombinations: [], payoutAmount: 0 };
    onPayoutConfigChange({
      ...payoutConfig,
      badNumbersConfig: {
        ...currentConfig,
        ...updates,
      },
    });
  };

  // Toggle a bad combination
  const toggleBadCombo = (combo: string) => {
    const newCombos = selectedBadCombos.includes(combo)
      ? selectedBadCombos.filter(c => c !== combo)
      : [...selectedBadCombos, combo].sort();
    setSelectedBadCombos(newCombos);
    updateBadNumbersConfig({ badCombinations: newCombos });
  };

  // Per-digit preset combinations (e.g., 2s only = 2-2, 2-5, 2-8, 2-9)
  // Each preset selects all combos where the FIRST digit (row) is the selected digit
  // and the SECOND digit (col) is one of the bad digits 2, 5, 8, or 9
  const BAD_DIGITS = [2, 5, 8, 9];
  const getDigitPresetCombos = (digit: number): string[] => {
    return BAD_DIGITS.map(c => `${digit}-${c}`);
  };

  // All 16 combinations (all bad digits × all bad digits)
  const ALL_BAD_COMBOS = BAD_DIGITS.flatMap(r => BAD_DIGITS.map(c => `${r}-${c}`));

  const DIGIT_PRESETS: { digit: number; label: string; combos: string[] }[] = BAD_DIGITS.map(d => ({
    digit: d,
    label: `${d}s only`,
    combos: getDigitPresetCombos(d),
  }));

  // Check if all 16 preset is active
  const isAllPresetActive = selectedBadCombos.length === 16 &&
    ALL_BAD_COMBOS.every(c => selectedBadCombos.includes(c));

  // Check if a specific digit preset is active (all 4 combos for that digit selected, nothing else)
  const isDigitPresetActive = (digit: number): boolean => {
    const presetCombos = getDigitPresetCombos(digit);
    return selectedBadCombos.length === 4 &&
      presetCombos.every(c => selectedBadCombos.includes(c));
  };

  // Get the last quarter key for OT display
  const lastQuarterKey = quarterKeys[quarterKeys.length - 1];
  const lastQuarterLabel = quarterLabels[quarterLabels.length - 1]?.label || 'Q4';
  const otAmount = payoutConfig.overtimeConfig?.overtimeAmount ?? 0;
  // Get Q4 payout for calculating regulation payout when OT is enabled
  const q4Payout = payoutConfig.quarterPayouts?.[lastQuarterKey] || 0;
  const regulationPayout = Math.max(0, q4Payout - otAmount);

  const updateQuarterPayout = (key: string, dollarValue: number) => {
    const newAmount = Math.max(0, Math.min(dollarValue, payoutPot));
    onPayoutConfigChange({
      ...payoutConfig,
      quarterPayouts: {
        ...(payoutConfig.quarterPayouts || {}),
        [key]: newAmount,
      },
    });
  };

  const updateQuarterWrongWayPayout = (key: string, dollarValue: number) => {
    const newAmount = Math.max(0, Math.min(dollarValue, payoutPot));
    onPayoutConfigChange({
      ...payoutConfig,
      quarterWrongWayPayouts: {
        ...(payoutConfig.quarterWrongWayPayouts || {}),
        [key]: newAmount,
      },
    });
  };

  const updateQuarterPlusNPayout = (key: string, dollarValue: number) => {
    const newAmount = Math.max(0, Math.min(dollarValue, payoutPot));
    onPayoutConfigChange({
      ...payoutConfig,
      plusFiveEnabled: true, // Enable the rule when any quarter payout is set
      quarterPlusNPayouts: {
        ...(payoutConfig.quarterPlusNPayouts || {}),
        [key]: newAmount,
      },
    });
  };

  const updateQuarterPlusNWrongWayPayout = (key: string, dollarValue: number) => {
    const newAmount = Math.max(0, Math.min(dollarValue, payoutPot));
    onPayoutConfigChange({
      ...payoutConfig,
      plusFiveEnabled: true, // Enable the rule when any quarter payout is set
      quarterPlusNWrongWayPayouts: {
        ...(payoutConfig.quarterPlusNWrongWayPayouts || {}),
        [key]: newAmount,
      },
    });
  };

  // Neighbor payout update functions
  const updateNeighborConfig = (updates: Partial<NeighborConfig>) => {
    onPayoutConfigChange({
      ...payoutConfig,
      neighborPayouts: {
        ...(payoutConfig.neighborPayouts || { enabled: false }),
        ...updates,
      },
    });
  };

  const toggleNeighborDirection = (direction: NeighborDirection) => {
    const current = neighborConfig?.directions || [];
    const newDirections = current.includes(direction)
      ? current.filter(d => d !== direction)
      : [...current, direction];
    updateNeighborConfig({ directions: newDirections });
  };

  const setAllCardinalDirections = () => {
    updateNeighborConfig({ directions: [...CARDINAL_DIRECTIONS] });
  };

  const setAllDiagonalDirections = () => {
    updateNeighborConfig({ directions: [...DIAGONAL_DIRECTIONS] });
  };

  const setAllDirections = () => {
    updateNeighborConfig({ directions: [...ALL_NEIGHBOR_DIRECTIONS] });
  };

  const clearDirections = () => {
    updateNeighborConfig({ directions: [] });
  };

  const updateNeighborQuarterAmount = (key: string, dollarValue: number) => {
    const newAmount = Math.max(0, Math.min(dollarValue, payoutPot));
    updateNeighborConfig({
      quarterAmounts: {
        ...(neighborConfig?.quarterAmounts || {}),
        [key]: newAmount,
      },
    });
  };

  // Box neighbor payout update functions
  const boxNeighborConfig = payoutConfig.boxNeighborPayouts;

  const updateBoxNeighborConfig = (updates: Partial<BoxNeighborConfig>) => {
    onPayoutConfigChange({
      ...payoutConfig,
      boxNeighborPayouts: {
        ...(payoutConfig.boxNeighborPayouts || { enabled: false, boxSize: '2x2' }),
        ...updates,
      } as BoxNeighborConfig,
    });
  };

  const updateBoxNeighborQuarterAmount = (key: string, dollarValue: number) => {
    const newAmount = Math.max(0, Math.min(dollarValue, payoutPot));
    updateBoxNeighborConfig({
      quarterAmounts: {
        ...(boxNeighborConfig?.quarterAmounts || {}),
        [key]: newAmount,
      },
    });
  };

  // Custom category management functions
  const addCustomCategory = () => {
    const newCategory: CustomPayoutCategory = {
      id: generateId(),
      name: '',
      payoutAmount: 0,
      displayOrder: (payoutConfig.customCategories?.length || 0) + 1,
    };
    onPayoutConfigChange({
      ...payoutConfig,
      customCategories: [...(payoutConfig.customCategories || []), newCategory],
    });
  };

  const updateCustomCategory = (id: string, updates: Partial<CustomPayoutCategory>) => {
    const categories = payoutConfig.customCategories || [];
    const updatedCategories = categories.map((cat) =>
      cat.id === id ? { ...cat, ...updates } : cat
    );
    onPayoutConfigChange({
      ...payoutConfig,
      customCategories: updatedCategories,
    });
  };

  const removeCustomCategory = (id: string) => {
    const categories = payoutConfig.customCategories || [];
    onPayoutConfigChange({
      ...payoutConfig,
      customCategories: categories.filter((cat) => cat.id !== id),
    });
  };

  // Calculate max profit in dollars (50% of total pot)
  const maxProfitDollars = totalPot * (MAX_PROFIT_PERCENT / 100);

  // Handle profit change from dollar amount
  const handleProfitDollarChange = (dollarAmount: number) => {
    // Convert dollars to percentage and clamp
    const newPercent = totalPot > 0 ? (dollarAmount / totalPot) * 100 : 0;
    const clampedPercent = Math.max(0, Math.min(newPercent, MAX_PROFIT_PERCENT));
    onProfitPercentChange(clampedPercent);
  };

  // Use custom hook for profit input in dollars
  const profitDollarInput = useNumberInput(profitAmount, handleProfitDollarChange, 0, maxProfitDollars);

  // Use custom hook for OT amount input in dollars
  const otAmountInput = useNumberInput(
    otAmount,
    (value) => {
      onPayoutConfigChange({
        ...payoutConfig,
        overtimeConfig: {
          enabled: true,
          overtimeAmount: value,
        },
      });
    },
    0,
    q4Payout
  );

  // Use custom hook for price per square input
  const pricePerSquareInput = useNumberInput(
    pricePerSquare,
    (value) => {
      if (onPricePerSquareChange) {
        onPricePerSquareChange(value);
      }
    },
    1,
    1000
  );

  if (readOnly) {
    // Check if we have any wrong-way or +N payouts to show those columns
    const hasWrongWayPayouts = quarterKeys.some(key => (payoutConfig.quarterWrongWayPayouts?.[key] || 0) > 0);
    const hasPlusNPayouts = quarterKeys.some(key => (payoutConfig.quarterPlusNPayouts?.[key] || 0) > 0);
    const customCategories = payoutConfig.customCategories || [];

    return (
      <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="overline" color="text.secondary">Total Pot</Typography>
            <Typography variant="body1">${totalPot.toFixed(2)}</Typography>
          </Box>
          {!hideProfit && (
            <Box>
              <Typography variant="overline" color="text.secondary">Profit ({profitPercent.toFixed(0)}%)</Typography>
              <Typography variant="body1">${profitAmount.toFixed(2)}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="overline" color="text.secondary">Payout Pool</Typography>
            <Typography variant="body1" fontWeight="bold">${payoutPot.toFixed(2)}</Typography>
          </Box>
        </Box>

        {/* Payouts Table */}
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem',
            mb: 2,
            '& th, & td': {
              border: '1px solid',
              borderColor: 'divider',
              px: 1.5,
              py: 1,
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: 'grey.100',
              fontWeight: 'bold',
            },
          }}
        >
          <thead>
            <tr>
              <th>Period</th>
              <th>Main</th>
              {hasWrongWayPayouts && <th>Wrong Way</th>}
              {hasPlusNPayouts && <th>+{payoutConfig.plusNOffset ?? 5}</th>}
            </tr>
          </thead>
          <tbody>
            {quarterLabels.map(({ key, label }) => {
              const mainAmount = payoutConfig.quarterPayouts?.[key] || 0;
              const wrongWayAmount = payoutConfig.quarterWrongWayPayouts?.[key] || 0;
              const plusNAmount = payoutConfig.quarterPlusNPayouts?.[key] || 0;

              return (
                <tr key={key}>
                  <td>{label}</td>
                  <td>${mainAmount.toFixed(2)}</td>
                  {hasWrongWayPayouts && (
                    <td>{wrongWayAmount > 0 ? `$${wrongWayAmount.toFixed(2)}` : '-'}</td>
                  )}
                  {hasPlusNPayouts && (
                    <td>{plusNAmount > 0 ? `$${plusNAmount.toFixed(2)}` : '-'}</td>
                  )}
                </tr>
              );
            })}
            {/* OT row if enabled */}
            {payoutConfig.overtimeConfig?.enabled && (
              <tr>
                <td>Final (OT)</td>
                <td>${(payoutConfig.overtimeConfig.overtimeAmount ?? 0).toFixed(2)}</td>
                {hasWrongWayPayouts && <td>-</td>}
                {hasPlusNPayouts && <td>-</td>}
              </tr>
            )}
          </tbody>
        </Box>

        {/* Neighbor Payouts */}
        {payoutConfig.neighborPayouts?.enabled && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Neighbor Payouts
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {/* Visual 3x3 grid showing directions */}
              {(() => {
                const enabledDirections = payoutConfig.neighborPayouts?.directions?.length
                  ? new Set(payoutConfig.neighborPayouts.directions)
                  : payoutConfig.neighborPayouts?.type === 'box'
                    ? new Set(ALL_NEIGHBOR_DIRECTIONS)
                    : new Set(CARDINAL_DIRECTIONS);

                // Grid positions: [row][col] => direction or 'WIN' for center
                const gridLayout: (NeighborDirection | 'WIN' | null)[][] = [
                  ['NW', 'N', 'NE'],
                  ['W', 'WIN', 'E'],
                  ['SW', 'S', 'SE'],
                ];

                return (
                  <Box sx={{ display: 'inline-block' }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 36px)',
                        gridTemplateRows: 'repeat(3, 36px)',
                        gap: '2px',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 0.5,
                        bgcolor: 'grey.100',
                      }}
                    >
                      {gridLayout.flat().map((cell, idx) => {
                        const isCenter = cell === 'WIN';
                        const isEnabled = cell !== 'WIN' && cell !== null && enabledDirections.has(cell);

                        return (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 0.5,
                              bgcolor: isCenter
                                ? 'success.main'
                                : isEnabled
                                  ? 'info.light'
                                  : 'grey.300',
                              color: isCenter
                                ? 'success.contrastText'
                                : isEnabled
                                  ? 'info.contrastText'
                                  : 'text.disabled',
                              fontWeight: isCenter || isEnabled ? 'bold' : 'normal',
                              fontSize: '0.65rem',
                            }}
                          >
                            {isCenter ? 'WIN' : cell}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })()}

              {/* Payout amounts */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {payoutConfig.neighborPayouts.usePerQuarterAmounts && payoutConfig.neighborPayouts.quarterAmounts ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {quarterLabels.map(({ key, label }) => {
                      const amount = payoutConfig.neighborPayouts?.quarterAmounts?.[key] || 0;
                      return amount > 0 ? (
                        <Typography key={key} variant="body2">
                          {label}: ${amount.toFixed(2)}/neighbor
                        </Typography>
                      ) : null;
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2">
                    ${(payoutConfig.neighborPayouts.globalAmount || 0).toFixed(2)} per neighbor per quarter
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Total neighbor allocation: ${neighborAllocatedDollars.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Box Neighbor Payouts - Read Only */}
        {payoutConfig.boxNeighborPayouts?.enabled && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Box Neighbor Payouts
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {/* Visual box grid preview */}
              {(() => {
                const boxSize = payoutConfig.boxNeighborPayouts?.boxSize || '2x2';
                // 2x2: 5 cols x 5 rows of 4-square boxes
                // 5x2: 2 cols x 5 rows of 10-square boxes
                // 5x5: 2 cols x 2 rows of 25-square quadrants
                const boxWidth = boxSize === '2x2' ? 2 : 5;
                const boxHeight = boxSize === '5x5' ? 5 : 2;
                const colGroups = 10 / boxWidth;
                const rowGroups = 10 / boxHeight;
                const squaresPerBox = boxWidth * boxHeight;

                return (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${colGroups}, 1fr)`,
                      gridTemplateRows: `repeat(${rowGroups}, 1fr)`,
                      gap: '2px',
                      width: boxSize === '2x2' ? 100 : boxSize === '5x5' ? 100 : 140,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 0.5,
                      bgcolor: 'grey.100',
                    }}
                  >
                    {Array.from({ length: rowGroups * colGroups }).map((_, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          aspectRatio: boxSize === '2x2' ? '1' : boxSize === '5x5' ? '1' : '2.5',
                          bgcolor: idx % 2 === (Math.floor(idx / colGroups) % 2) ? 'info.light' : 'info.main',
                          borderRadius: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.55rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {squaresPerBox}
                      </Box>
                    ))}
                  </Box>
                );
              })()}

              {/* Payout details */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2">
                  Box Size: {payoutConfig.boxNeighborPayouts.boxSize} ({getBoxNeighborCount(payoutConfig.boxNeighborPayouts.boxSize)} neighbors)
                </Typography>
                {payoutConfig.boxNeighborPayouts.usePerQuarterAmounts && payoutConfig.boxNeighborPayouts.quarterAmounts ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {quarterLabels.map(({ key, label }) => {
                      const amount = payoutConfig.boxNeighborPayouts?.quarterAmounts?.[key] || 0;
                      return amount > 0 ? (
                        <Typography key={key} variant="body2">
                          {label}: ${amount.toFixed(2)}/neighbor
                        </Typography>
                      ) : null;
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2">
                    ${(payoutConfig.boxNeighborPayouts.globalAmount || 0).toFixed(2)} per neighbor per quarter
                  </Typography>
                )}
                {(() => {
                  const neighborCount = getBoxNeighborCount(payoutConfig.boxNeighborPayouts?.boxSize || '2x2');
                  let amountSumAcrossQuarters: number;
                  if (payoutConfig.boxNeighborPayouts?.usePerQuarterAmounts && payoutConfig.boxNeighborPayouts?.quarterAmounts) {
                    // Sum of all per-quarter amounts (each neighbor gets this total across all quarters)
                    amountSumAcrossQuarters = Object.values(payoutConfig.boxNeighborPayouts.quarterAmounts).reduce((sum, amt) => sum + (amt || 0), 0);
                  } else {
                    // Global amount × number of quarters
                    amountSumAcrossQuarters = (payoutConfig.boxNeighborPayouts?.globalAmount || 0) * quarterLabels.length;
                  }
                  const totalAllocation = amountSumAcrossQuarters * neighborCount;

                  return (
                    <Typography variant="caption" color="text.secondary">
                      Total box neighbor allocation: ${totalAllocation.toFixed(2)}
                    </Typography>
                  );
                })()}
              </Box>
            </Box>
          </Box>
        )}

        {/* Custom Payouts */}
        {customCategories.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Custom Payouts
            </Typography>
            {customCategories.map((cat) => (
              <Box
                key={cat.id}
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 0.5,
                  mb: 0.5,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flexShrink: 1,
                    minWidth: 0,
                  }}
                  title={cat.name}
                >
                  {cat.name}
                </Typography>
                <Typography variant="body2" sx={{ flexShrink: 0 }}>
                  : ${cat.payoutAmount.toFixed(2)}
                </Typography>
                {cat.wrongWayEnabled && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    (2-way: ${(cat.payoutAmount * 2).toFixed(2)} total)
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Bad Numbers - Read Only */}
        {payoutConfig.badNumbersConfig?.enabled && (() => {
          const config = payoutConfig.badNumbersConfig;
          const badCombinations = config.badCombinations || [];
          // Legacy fallback: generate combinations from badDigits
          const badDigits = config.badDigits || [];
          const effectiveCombos = badCombinations.length > 0
            ? badCombinations
            : badDigits.flatMap(r => badDigits.map(c => `${r}-${c}`));
          const badSquareCount = effectiveCombos.length;

          return (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Bad Numbers
              </Typography>
              <Typography variant="body2">
                Payout per square: ${config.payoutAmount?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2">
                Total bad squares: {badSquareCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total bad numbers allocation: ${badNumbersAllocatedDollars.toFixed(2)}
              </Typography>

              {/* Bad Combinations Display */}
              {effectiveCombos.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                    Bad Combinations:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {effectiveCombos.sort().map((combo) => (
                      <Chip
                        key={combo}
                        label={combo}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          bgcolor: '#ef5350',
                          color: '#fff',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })()}
      </Box>
    );
  }

  if (compact) {
    // Compact mode with sliders (used in Settings page)
    return (
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="overline" color="text.secondary">Gross Pot</Typography>
            <Typography variant="h6">${totalPot.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Profit</Typography>
            <Typography variant="h6" color="success.main">${profitAmount.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Payout Pot</Typography>
            <Typography variant="h6" color="primary">${payoutPot.toFixed(2)}</Typography>
          </Box>
        </Box>

        {/* Profit Margin Slider - in dollars */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Organizer Profit: ${profitAmount.toFixed(2)} ({profitPercent.toFixed(0)}%)
          </Typography>
          <Slider
            value={profitAmount}
            onChange={(_, value) => handleProfitDollarChange(value as number)}
            min={0}
            max={maxProfitDollars}
            step={totalPot > 100 ? 5 : 1}
            marks={[
              { value: 0, label: '$0' },
              { value: maxProfitDollars / 2, label: `$${(maxProfitDollars / 2).toFixed(0)}` },
              { value: maxProfitDollars, label: `$${maxProfitDollars.toFixed(0)} (max)` },
            ]}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Compact Mode Allocation Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(payoutPot > 0 ? (allocatedDollars / payoutPot) * 100 : 0, 100)}
            color={allocatedDollars > payoutPot + 0.01 ? 'error' : Math.abs(remainingDollars) < 0.01 ? 'success' : 'primary'}
            sx={{ height: 10, borderRadius: 1, mb: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Allocated: ${allocatedDollars.toFixed(2)}
            </Typography>
            <Typography
              variant="body2"
              fontWeight="medium"
              color={allocatedDollars > payoutPot + 0.01 ? 'error' : Math.abs(remainingDollars) < 0.01 ? 'success.main' : 'warning.main'}
            >
              {allocatedDollars > payoutPot + 0.01
                ? `Over by $${Math.abs(remainingDollars).toFixed(2)}`
                : Math.abs(remainingDollars) < 0.01
                  ? '100% Allocated'
                  : `$${remainingDollars.toFixed(2)} unallocated`}
            </Typography>
          </Box>
        </Box>

        {/* Right Way Section */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={rightWayEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setRightWayEnabled(enabled);
                  onPayoutConfigChange({
                    ...payoutConfig,
                    rightWayEnabled: enabled,
                    quarterPayouts: enabled ? payoutConfig.quarterPayouts : {},
                  });
                }}
              />
            }
            label="Right Way Payouts"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            Right Way pays to the square owner whose numbers match the last digit of each team's score.
          </Typography>
          <Collapse in={rightWayEnabled}>
            <Box sx={{ ml: 4 }}>
              {quarterLabels.map(({ key, label }) => {
                const quarterAmount = payoutConfig.quarterPayouts?.[key] || 0;
                const quarterPercent = payoutPot > 0 ? (quarterAmount / payoutPot) * 100 : 0;

                return (
                  <Box key={key} sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      {label}: ${quarterAmount.toFixed(2)} ({quarterPercent.toFixed(0)}%)
                    </Typography>
                    <Slider
                      value={quarterAmount}
                      onChange={(_, value) => updateQuarterPayout(key, value as number)}
                      min={0}
                      max={payoutPot}
                      step={payoutPot > 100 ? 5 : 1}
                    />
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Wrong Way Section */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={wrongWayEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setWrongWayEnabled(enabled);
                  onPayoutConfigChange({
                    ...payoutConfig,
                    wrongWayEnabled: enabled,
                    quarterWrongWayPayouts: enabled ? payoutConfig.quarterWrongWayPayouts : {},
                  });
                }}
              />
            }
            label="Wrong Way Payouts"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            Wrong Way pays a bonus to the square owner whose numbers match the score in reverse.
          </Typography>
          <Collapse in={wrongWayEnabled}>
            <Box sx={{ ml: 4 }}>
              {quarterLabels.map(({ key, label }) => {
                const wrongWayAmount = payoutConfig.quarterWrongWayPayouts?.[key] || 0;

                return (
                  <Box key={key} sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 60 }}>
                        {label}:
                      </Typography>
                      <TextField
                        type="number"
                        value={wrongWayAmount || ''}
                        onChange={(e) => updateQuarterWrongWayPayout(key, parseFloat(e.target.value) || 0)}
                        size="small"
                        sx={{ width: 100 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        placeholder="0"
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Plus N Rule Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={plusNEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setPlusNEnabled(enabled);
                    if (enabled) {
                      onPayoutConfigChange({
                        ...payoutConfig,
                        plusFiveEnabled: true,
                        plusNOffset: payoutConfig.plusNOffset ?? 5,
                        plusNRightWayEnabled: plusNRightWayEnabled,
                        plusNWrongWayEnabled: plusNWrongWayEnabled,
                      });
                    } else {
                      onPayoutConfigChange({
                        ...payoutConfig,
                        plusFiveEnabled: false,
                        quarterPlusNPayouts: {},
                        plusNRightWayEnabled: false,
                        plusNWrongWayEnabled: false,
                      });
                    }
                  }}
                />
              }
              label={`+${payoutConfig.plusNOffset ?? 5} Rule`}
            />
            {plusNEnabled && (
              <TextField
                label="Offset"
                type="number"
                value={payoutConfig.plusNOffset ?? 5}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 5;
                  const clamped = Math.max(1, Math.min(9, value));
                  onPayoutConfigChange({
                    ...payoutConfig,
                    plusNOffset: clamped,
                  });
                }}
                size="small"
                sx={{ width: 80 }}
                inputProps={{ min: 1, max: 9 }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            The +{payoutConfig.plusNOffset ?? 5} Rule pays a bonus when the winning square's numbers each differ by {payoutConfig.plusNOffset ?? 5} from the score digits
            (e.g., if score ends in 2-7, the +5 square is 7-2).
          </Typography>
          <Collapse in={plusNEnabled}>
            <Box sx={{ ml: 4 }}>
              {/* Apply to Right Way */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={plusNRightWayEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setPlusNRightWayEnabled(enabled);
                        onPayoutConfigChange({
                          ...payoutConfig,
                          plusNRightWayEnabled: enabled,
                        });
                      }}
                    />
                  }
                  label={<Typography variant="body2">Apply to Right Way</Typography>}
                />
                <Collapse in={plusNRightWayEnabled}>
                  <Box sx={{ ml: 4 }}>
                    {quarterLabels.map(({ key, label }) => {
                      const plusNAmount = payoutConfig.quarterPlusNPayouts?.[key] || 0;

                      return (
                        <Box key={key} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: 60 }}>
                              {label}:
                            </Typography>
                            <TextField
                              type="number"
                              value={plusNAmount || ''}
                              onChange={(e) => updateQuarterPlusNPayout(key, parseFloat(e.target.value) || 0)}
                              size="small"
                              sx={{ width: 100 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                              placeholder="0"
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>

              {/* Apply to Wrong Way */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={plusNWrongWayEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setPlusNWrongWayEnabled(enabled);
                        onPayoutConfigChange({
                          ...payoutConfig,
                          plusNWrongWayEnabled: enabled,
                          quarterPlusNWrongWayPayouts: enabled ? payoutConfig.quarterPlusNWrongWayPayouts : {},
                        });
                      }}
                    />
                  }
                  label={<Typography variant="body2">Apply to Wrong Way</Typography>}
                />
                <Collapse in={plusNWrongWayEnabled}>
                  <Box sx={{ ml: 4 }}>
                    {quarterLabels.map(({ key, label }) => {
                      const plusNWWAmount = payoutConfig.quarterPlusNWrongWayPayouts?.[key] || 0;

                      return (
                        <Box key={key} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ minWidth: 60 }}>
                              {label}:
                            </Typography>
                            <TextField
                              type="number"
                              value={plusNWWAmount || ''}
                              onChange={(e) => updateQuarterPlusNWrongWayPayout(key, parseFloat(e.target.value) || 0)}
                              size="small"
                              sx={{ width: 100 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                              placeholder="0"
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ my: 2 }} />

        {cellMode !== 'standard' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>100-Cell Boards Only:</strong> Neighbor payouts (both regular and box) require a 100-cell board because neighbor relationships must align with individual square ownership.
          </Alert>
        )}

        {/* Neighbor Payouts Section */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={neighborEnabled}
                disabled={cellMode !== 'standard'}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setNeighborEnabled(enabled);
                  if (enabled) {
                    // Disable box neighbors when enabling wraparound neighbors
                    // Use single onPayoutConfigChange call to ensure both updates happen atomically
                    if (boxNeighborEnabled) {
                      setBoxNeighborEnabled(false);
                    }
                    onPayoutConfigChange({
                      ...payoutConfig,
                      boxNeighborPayouts: {
                        ...(payoutConfig.boxNeighborPayouts || { enabled: false, boxSize: '2x2' }),
                        enabled: false,
                      } as BoxNeighborConfig,
                      neighborPayouts: {
                        ...(payoutConfig.neighborPayouts || { enabled: false }),
                        enabled: true,
                        directions: neighborConfig?.directions?.length ? neighborConfig.directions : CARDINAL_DIRECTIONS,
                      },
                    });
                  } else {
                    updateNeighborConfig({ enabled: false });
                  }
                }}
              />
            }
            label="Neighbor Payouts"
          />
          <Box sx={{ ml: 4, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Neighbor payouts reward squares adjacent to the winning square. The grid wraps around (toroidal).
            </Typography>
            <Tooltip title="Learn more about neighbor payouts">
              <IconButton
                component={Link}
                to="/support/payouts#neighbor-payouts"
                target="_blank"
                rel="noopener"
                size="small"
                sx={{ p: 0.5 }}
              >
                <HelpIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          </Box>
          <Collapse in={neighborEnabled}>
            <Box sx={{ ml: 4 }}>
              {/* Direction Selection */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>Click cells to select directions:</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {/* 3x3 Clickable Grid */}
                  {(() => {
                    const selectedDirections = new Set(neighborConfig?.directions || []);
                    const gridLayout: (NeighborDirection | 'WIN')[][] = [
                      ['NW', 'N', 'NE'],
                      ['W', 'WIN', 'E'],
                      ['SW', 'S', 'SE'],
                    ];

                    return (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 44px)',
                          gridTemplateRows: 'repeat(3, 44px)',
                          gap: '3px',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 0.5,
                          bgcolor: 'grey.100',
                        }}
                      >
                        {gridLayout.flat().map((cell, idx) => {
                          const isCenter = cell === 'WIN';
                          const isSelected = !isCenter && selectedDirections.has(cell);

                          return (
                            <Box
                              key={idx}
                              onClick={isCenter ? undefined : () => toggleNeighborDirection(cell)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 0.5,
                                cursor: isCenter ? 'default' : 'pointer',
                                bgcolor: isCenter
                                  ? 'success.main'
                                  : isSelected
                                    ? 'info.main'
                                    : 'grey.300',
                                color: isCenter || isSelected ? 'white' : 'text.secondary',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                transition: 'all 0.15s ease',
                                '&:hover': isCenter ? {} : {
                                  bgcolor: isSelected ? 'info.dark' : 'grey.400',
                                  transform: 'scale(1.05)',
                                },
                                userSelect: 'none',
                              }}
                            >
                              {isCenter ? 'WIN' : cell}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()}

                  {/* Quick selection buttons */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>Quick select:</Typography>
                    <Button size="small" variant="outlined" onClick={setAllCardinalDirections} sx={{ justifyContent: 'flex-start' }}>
                      Cardinal (N, S, E, W)
                    </Button>
                    <Button size="small" variant="outlined" onClick={setAllDiagonalDirections} sx={{ justifyContent: 'flex-start' }}>
                      Diagonal (NE, SE, SW, NW)
                    </Button>
                    <Button size="small" variant="outlined" onClick={setAllDirections} sx={{ justifyContent: 'flex-start' }}>
                      All 8 Directions
                    </Button>
                    <Button size="small" variant="outlined" onClick={clearDirections} sx={{ justifyContent: 'flex-start' }}>
                      Clear
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* Payout Amount Configuration */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={neighborUsePerQuarter}
                      onChange={(e) => {
                        const usePerQuarter = e.target.checked;
                        setNeighborUsePerQuarter(usePerQuarter);
                        updateNeighborConfig({ usePerQuarterAmounts: usePerQuarter });
                      }}
                    />
                  }
                  label={<Typography variant="body2">Per-quarter amounts</Typography>}
                />
              </Box>

              {!neighborUsePerQuarter ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Amount per neighbor:
                  </Typography>
                  <TextField
                    type="number"
                    value={neighborConfig?.globalAmount || ''}
                    onChange={(e) => updateNeighborConfig({ globalAmount: parseFloat(e.target.value) || 0 })}
                    size="small"
                    sx={{ width: 100 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    placeholder="0"
                  />
                </Box>
              ) : (
                <Box>
                  {quarterLabels.map(({ key, label }) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 60 }}>
                        {label}:
                      </Typography>
                      <TextField
                        type="number"
                        value={neighborConfig?.quarterAmounts?.[key] || ''}
                        onChange={(e) => updateNeighborQuarterAmount(key, parseFloat(e.target.value) || 0)}
                        size="small"
                        sx={{ width: 100 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        placeholder="0"
                      />
                    </Box>
                  ))}
                </Box>
              )}

              {neighborAllocatedDollars > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Total neighbor allocation: ${neighborAllocatedDollars.toFixed(2)} ({neighborDirections.length} directions)
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Box Neighbor Payouts Section */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={boxNeighborEnabled}
                disabled={cellMode !== 'standard'}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setBoxNeighborEnabled(enabled);
                  if (enabled) {
                    // Disable wraparound neighbors when enabling box neighbors
                    // Use single onPayoutConfigChange call to ensure both updates happen atomically
                    if (neighborEnabled) {
                      setNeighborEnabled(false);
                    }
                    onPayoutConfigChange({
                      ...payoutConfig,
                      neighborPayouts: {
                        ...(payoutConfig.neighborPayouts || { enabled: false }),
                        enabled: false,
                      },
                      boxNeighborPayouts: {
                        ...(payoutConfig.boxNeighborPayouts || { enabled: false, boxSize: '2x2' }),
                        enabled: true,
                        boxSize: boxNeighborConfig?.boxSize || '2x2',
                      } as BoxNeighborConfig,
                    });
                  } else {
                    updateBoxNeighborConfig({ enabled: false });
                  }
                }}
              />
            }
            label="Box Neighbor Payouts"
          />
          <Box sx={{ ml: 4, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Divides the grid into fixed rectangular groups. When a square wins, its box neighbors get paid.
            </Typography>
            <Tooltip title="Learn more about box neighbor payouts">
              <IconButton
                component={Link}
                to="/support/payouts#box-neighbor-payouts"
                target="_blank"
                rel="noopener"
                size="small"
                sx={{ p: 0.5 }}
              >
                <HelpIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          </Box>

          {neighborEnabled && cellMode === 'standard' && (
            <Typography variant="body2" color="warning.main" sx={{ ml: 4, mb: 1 }}>
              Note: You cannot use both wraparound neighbors and box neighbors on the same board.
            </Typography>
          )}

          <Collapse in={boxNeighborEnabled}>
            <Box sx={{ ml: 4 }}>
              {/* Box Size Selection */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>Box Size:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {BOX_NEIGHBOR_SIZES.map(({ value, label, neighborCount }) => (
                    <Chip
                      key={value}
                      label={`${label} (${neighborCount} neighbors)`}
                      onClick={() => updateBoxNeighborConfig({ boxSize: value })}
                      color={boxNeighborConfig?.boxSize === value ? 'primary' : 'default'}
                      variant={boxNeighborConfig?.boxSize === value ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {BOX_NEIGHBOR_SIZES.find(s => s.value === (boxNeighborConfig?.boxSize || '2x2'))?.description}
                </Typography>
              </Box>

              {/* Visual Box Preview */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>Box Layout Preview:</Typography>
                {(() => {
                  const boxSize = boxNeighborConfig?.boxSize || '2x2';
                  // 2x2: 5 cols x 5 rows of 4-square boxes
                  // 5x2: 2 cols x 5 rows of 10-square boxes
                  // 5x5: 2 cols x 2 rows of 25-square quadrants
                  const boxWidth = boxSize === '2x2' ? 2 : 5;
                  const boxHeight = boxSize === '5x5' ? 5 : 2;
                  const colGroups = 10 / boxWidth;
                  const rowGroups = 10 / boxHeight;
                  const squaresPerBox = boxWidth * boxHeight;

                  return (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${colGroups}, 1fr)`,
                        gridTemplateRows: `repeat(${rowGroups}, 1fr)`,
                        gap: '2px',
                        width: boxSize === '2x2' ? 150 : boxSize === '5x5' ? 150 : 200,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 0.5,
                        bgcolor: 'grey.100',
                      }}
                    >
                      {Array.from({ length: rowGroups * colGroups }).map((_, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            aspectRatio: boxSize === '2x2' ? '1' : boxSize === '5x5' ? '1' : '2.5',
                            bgcolor: idx % 2 === (Math.floor(idx / colGroups) % 2) ? 'info.light' : 'info.main',
                            borderRadius: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {squaresPerBox}
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>

              {/* Payout Amount Configuration */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={boxNeighborUsePerQuarter}
                      onChange={(e) => {
                        const usePerQuarter = e.target.checked;
                        setBoxNeighborUsePerQuarter(usePerQuarter);
                        updateBoxNeighborConfig({ usePerQuarterAmounts: usePerQuarter });
                      }}
                    />
                  }
                  label={<Typography variant="body2">Per-quarter amounts</Typography>}
                />
              </Box>

              {!boxNeighborUsePerQuarter ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Amount per neighbor:
                  </Typography>
                  <TextField
                    type="number"
                    value={boxNeighborConfig?.globalAmount || ''}
                    onChange={(e) => updateBoxNeighborConfig({ globalAmount: parseFloat(e.target.value) || 0 })}
                    size="small"
                    sx={{ width: 100 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    placeholder="0"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Each neighbor gets this amount (not split)
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {quarterLabels.map(({ key, label }) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 60 }}>
                        {label}:
                      </Typography>
                      <TextField
                        type="number"
                        value={boxNeighborConfig?.quarterAmounts?.[key] || ''}
                        onChange={(e) => updateBoxNeighborQuarterAmount(key, parseFloat(e.target.value) || 0)}
                        size="small"
                        sx={{ width: 100 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        placeholder="0"
                      />
                    </Box>
                  ))}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Each neighbor gets the full amount for that quarter (not split)
                  </Typography>
                </Box>
              )}

              {/* Total calculation */}
              {(() => {
                const neighborCount = getBoxNeighborCount(boxNeighborConfig?.boxSize || '2x2');
                let amountSumAcrossQuarters: number;
                if (boxNeighborUsePerQuarter && boxNeighborConfig?.quarterAmounts) {
                  // Sum of all per-quarter amounts
                  amountSumAcrossQuarters = Object.values(boxNeighborConfig.quarterAmounts).reduce((sum, amt) => sum + (amt || 0), 0);
                } else {
                  // Global amount × number of quarters
                  amountSumAcrossQuarters = (boxNeighborConfig?.globalAmount || 0) * quarterLabels.length;
                }
                const totalAllocation = amountSumAcrossQuarters * neighborCount;

                return totalAllocation > 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Total box neighbor allocation: ${totalAllocation.toFixed(2)} ({neighborCount} neighbors x {quarterLabels.length} quarters)
                  </Typography>
                ) : null;
              })()}
            </Box>
          </Collapse>
        </Box>

        {allocatedDollars > payoutPot + 0.01 && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            Total payouts exceed the payout pot! Reduce quarter amounts.
          </Typography>
        )}

        {/* Additional Payout Options */}
        <Box sx={{ mt: 3, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Overtime Configuration - only show for 4 quarters */}
          {quarters === 4 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={payoutConfig.overtimeConfig?.enabled ?? false}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        if (enabled) {
                          onPayoutConfigChange({
                            ...payoutConfig,
                            overtimeConfig: {
                              enabled: true,
                              overtimeAmount: payoutConfig.overtimeConfig?.overtimeAmount ?? 0,
                            },
                          });
                        } else {
                          // Clear overtime amount when disabled
                          onPayoutConfigChange({
                            ...payoutConfig,
                            overtimeConfig: {
                              enabled: false,
                              overtimeAmount: 0,
                            },
                          });
                        }
                      }}
                    />
                  }
                  label="Enable OT split"
                />
                {payoutConfig.overtimeConfig?.enabled && (
                  <TextField
                    label="Final (OT) payout"
                    type="number"
                    value={otAmount || ''}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      const clamped = Math.max(0, Math.min(value, q4Payout));
                      onPayoutConfigChange({
                        ...payoutConfig,
                        overtimeConfig: {
                          enabled: true,
                          overtimeAmount: clamped,
                        },
                      });
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    size="small"
                    sx={{ width: 150 }}
                    placeholder="0"
                    helperText={regulationPayout > 0 ? `${lastQuarterKey}: $${regulationPayout.toFixed(2)}` : undefined}
                  />
                )}
              </Box>
              <Collapse in={payoutConfig.overtimeConfig?.enabled}>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                  If overtime occurs, the {lastQuarterLabel.split(' /')[0]} payout ($
                  {q4Payout.toFixed(2)}) is split: ${regulationPayout.toFixed(2)} for end of regulation,
                  ${otAmount.toFixed(2)} for final OT score.
                </Typography>
              </Collapse>
            </Box>
          )}
        </Box>

        {/* Bad Numbers Section */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={badNumbersEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setBadNumbersEnabled(enabled);
                    if (enabled) {
                      // Default to 2s only preset when enabling (if no combos already selected)
                      const defaultCombos = selectedBadCombos.length > 0 ? selectedBadCombos : getDigitPresetCombos(2);
                      setSelectedBadCombos(defaultCombos);
                      updateBadNumbersConfig({
                        enabled: true,
                        badCombinations: defaultCombos,
                        payoutAmount: badNumbersPayoutAmount || 10,
                      });
                    } else {
                      updateBadNumbersConfig({ enabled: false });
                    }
                  }}
                />
              }
              label="Bad Numbers"
            />
            {badNumbersEnabled && badNumbersAllocatedDollars > 0 && (
              <Typography variant="body2" color="text.secondary">
                {selectedBadCombos.length} squares, ${badNumbersAllocatedDollars.toFixed(2)} total
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            Compensate players with "unlucky" number combinations (right-way only).
            Payouts are credited automatically when the board locks.
          </Typography>
          <Collapse in={badNumbersEnabled}>
            <Box sx={{ ml: 4, mt: 2 }}>
              {/* Preset selector */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="2,5,8,9"
                    variant={isAllPresetActive ? 'filled' : 'outlined'}
                    color={isAllPresetActive ? 'primary' : 'default'}
                    onClick={() => {
                      setSelectedBadCombos([...ALL_BAD_COMBOS]);
                      updateBadNumbersConfig({ badCombinations: [...ALL_BAD_COMBOS] });
                    }}
                    size="small"
                  />
                  {DIGIT_PRESETS.map(({ digit, label }) => {
                    const combos = getDigitPresetCombos(digit);
                    return (
                      <Chip
                        key={digit}
                        label={label}
                        variant={isDigitPresetActive(digit) ? 'filled' : 'outlined'}
                        color={isDigitPresetActive(digit) ? 'primary' : 'default'}
                        onClick={() => {
                          setSelectedBadCombos([...combos]);
                          updateBadNumbersConfig({ badCombinations: [...combos] });
                        }}
                        size="small"
                      />
                    );
                  })}
                  <Chip
                    label="Clear All"
                    variant="outlined"
                    onClick={() => {
                      setSelectedBadCombos([]);
                      updateBadNumbersConfig({ badCombinations: [] });
                    }}
                    size="small"
                  />
                </Box>
              </Box>

              {/* Compact 10x10 Grid Selector */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Click cells to toggle bad combinations:
                </Typography>
                <Box sx={{
                  display: 'inline-grid',
                  gridTemplateColumns: 'auto repeat(10, 24px)',
                  gap: '2px',
                  bgcolor: 'grey.100',
                  p: 0.5,
                  borderRadius: 1,
                }}>
                  {/* Header row */}
                  <Box /> {/* Empty corner */}
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(col => (
                    <Box
                      key={`header-${col}`}
                      sx={{
                        width: 24,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.65rem',
                        fontWeight: 'bold',
                        color: 'text.secondary',
                      }}
                    >
                      {col}
                    </Box>
                  ))}

                  {/* Grid rows */}
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(row => (
                    <React.Fragment key={`row-${row}`}>
                      <Box
                        sx={{
                          width: 16,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.65rem',
                          fontWeight: 'bold',
                          color: 'text.secondary',
                        }}
                      >
                        {row}
                      </Box>
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(col => {
                        const combo = `${row}-${col}`;
                        const isSelected = selectedBadCombos.includes(combo);
                        return (
                          <Box
                            key={combo}
                            onClick={() => toggleBadCombo(combo)}
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: isSelected ? 'error.main' : 'background.paper',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.1s',
                              '&:hover': {
                                bgcolor: isSelected ? 'error.dark' : 'grey.300',
                              },
                            }}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>

              {/* Payout amount */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="body2">
                  Payout per square:
                </Typography>
                <TextField
                  type="number"
                  value={badNumbersPayoutAmount || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setBadNumbersPayoutAmount(value);
                    updateBadNumbersConfig({ payoutAmount: value });
                  }}
                  size="small"
                  sx={{ width: 120 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  placeholder="10"
                />
              </Box>

              {/* Summary */}
              {selectedBadCombos.length > 0 && badNumbersPayoutAmount > 0 && (
                <Box sx={{ p: 1.5, bgcolor: 'warning.light', borderRadius: 1, mt: 2 }}>
                  <Typography variant="body2">
                    <strong>{selectedBadCombos.length}</strong> bad squares
                    {' × '}
                    <strong>${badNumbersPayoutAmount.toFixed(2)}</strong>
                    {' = '}
                    <strong>${(selectedBadCombos.length * badNumbersPayoutAmount).toFixed(2)}</strong> total
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Custom Payout Categories */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">
              Custom Payouts
            </Typography>
            {(payoutConfig.customCategories?.length || 0) > 0 && (
              <Typography variant="body2" color="text.secondary">
                ({payoutConfig.customCategories?.length} categories, ${customAllocatedDollars.toFixed(2)})
              </Typography>
            )}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add custom stat-based payouts (e.g., Passing Yards, Rush Yards) for events like the Super Bowl.
        </Typography>
        {(payoutConfig.customCategories || []).map((category) => (
          <Box key={category.id} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', minWidth: 0, flexWrap: 'wrap' }}>
              <TextField
                label="Name"
                value={category.name}
                onChange={(e) => updateCustomCategory(category.id, { name: e.target.value })}
                size="small"
                sx={{ flex: 1, minWidth: 120 }}
                placeholder="e.g., Passing Yards"
              />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexShrink: 0 }}>
                <TextField
                  label="Amount"
                  type="number"
                  value={category.payoutAmount}
                  onChange={(e) => updateCustomCategory(category.id, { payoutAmount: parseFloat(e.target.value) || 0 })}
                  size="small"
                  sx={{ width: 120 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                <IconButton size="small" onClick={() => removeCustomCategory(category.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Tooltip title="When enabled, automatically pays both right-way and wrong-way winners (doubles the payout)">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={category.wrongWayEnabled || false}
                      onChange={(e) => updateCustomCategory(category.id, { wrongWayEnabled: e.target.checked })}
                    />
                  }
                  label={
                    <Typography variant="caption">
                      Wrong Way {category.wrongWayEnabled && category.payoutAmount > 0 && (
                        <Typography component="span" variant="caption" color="success.main">
                          (${category.payoutAmount.toFixed(2)} × 2 = ${(category.payoutAmount * 2).toFixed(2)} total)
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              </Tooltip>
            </Box>
          </Box>
        ))}
        <Button startIcon={<AddIcon />} onClick={addCustomCategory} size="small" variant="outlined">
          Add Custom Payout
        </Button>
      </Box>
    );
  }

  // Calculate allocation status for progress bar
  const allocationPercent = payoutPot > 0 ? Math.min((allocatedDollars / payoutPot) * 100, 100) : 0;
  const isFullyAllocated = payoutPot > 0 && Math.abs(remainingDollars) < 0.01;
  const isOverAllocated = allocatedDollars > payoutPot + 0.01;

  // Determine progress bar color
  const getProgressColor = (): 'error' | 'success' | 'primary' => {
    if (isOverAllocated) return 'error';
    if (isFullyAllocated) return 'success';
    return 'primary';
  };

  // Full mode with text fields (used in Create Board page)
  return (
    <Box sx={{ minWidth: 0 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 4, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="overline" color="text.secondary">Total Pot</Typography>
            <Typography variant="h5">${totalPot.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Profit ({profitPercent}%)</Typography>
            <Typography variant="h5" color="success.main">${profitAmount.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Payout Pot</Typography>
            <Typography variant="h5" color="primary">${payoutPot.toFixed(2)}</Typography>
          </Box>
        </Box>

        {/* Allocation Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(allocationPercent, 100)}
            color={getProgressColor()}
            sx={{ height: 12, borderRadius: 1, mb: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                Quarters: ${periodAllocatedDollars.toFixed(2)}
              </Typography>
              {quarterWrongWayAllocatedDollars > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Wrong Way: ${quarterWrongWayAllocatedDollars.toFixed(2)}
                </Typography>
              )}
              {(quarterPlusNAllocatedDollars > 0 || quarterPlusNWrongWayAllocatedDollars > 0) && (
                <Typography variant="caption" color="text.secondary">
                  +{payoutConfig.plusNOffset ?? 5}: ${(quarterPlusNAllocatedDollars + quarterPlusNWrongWayAllocatedDollars).toFixed(2)}
                </Typography>
              )}
              {customAllocatedDollars > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Custom: ${customAllocatedDollars.toFixed(2)}
                </Typography>
              )}
              {neighborAllocatedDollars > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Neighbors: ${neighborAllocatedDollars.toFixed(2)}
                </Typography>
              )}
            </Box>
            <Typography
              variant="body2"
              fontWeight="medium"
              color={isOverAllocated ? 'error' : isFullyAllocated ? 'success.main' : 'warning.main'}
            >
              {isOverAllocated
                ? `Over by $${Math.abs(remainingDollars).toFixed(2)}`
                : isFullyAllocated
                  ? '100% Allocated'
                  : `$${remainingDollars.toFixed(2)} unallocated`}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {onPricePerSquareChange && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Price Per Square"
                value={pricePerSquareInput.inputValue}
                onChange={pricePerSquareInput.handleChange}
                onBlur={pricePerSquareInput.handleBlur}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 1, max: 1000, 'data-testid': 'board-price-input' }}
                helperText={`Total pot: $${(pricePerSquare * totalCells).toFixed(2)} (${totalCells} squares)`}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Organizer Profit"
              value={profitDollarInput.inputValue}
              onChange={profitDollarInput.handleChange}
              onBlur={profitDollarInput.handleBlur}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              helperText={`${profitPercent.toFixed(0)}% of pot (max $${maxProfitDollars.toFixed(2)} / ${MAX_PROFIT_PERCENT}%)`}
            />
          </Grid>
        </Grid>

        {/* Right Way Section */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={rightWayEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setRightWayEnabled(enabled);
                  onPayoutConfigChange({
                    ...payoutConfig,
                    rightWayEnabled: enabled,
                    quarterPayouts: enabled ? payoutConfig.quarterPayouts : {},
                  });
                }}
              />
            }
            label="Right Way Payouts"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            Right Way pays to the square owner whose numbers match the last digit of each team's score (home-away).
          </Typography>
          <Collapse in={rightWayEnabled}>
            <Box sx={{ ml: 4 }}>
              <Grid container spacing={2}>
                {quarterLabels.map(({ key, label }) => (
                  <QuarterPayoutInput
                    key={key}
                    quarterKey={key}
                    label={label}
                    dollarAmount={payoutConfig.quarterPayouts?.[key] || 0}
                    wrongWayAmount={0}
                    plusNAmount={0}
                    plusNOffset={payoutConfig.plusNOffset ?? 5}
                    payoutPot={payoutPot}
                    showWrongWay={false}
                    showPlusN={false}
                    onUpdate={updateQuarterPayout}
                    onUpdateWrongWay={updateQuarterWrongWayPayout}
                    onUpdatePlusN={updateQuarterPlusNPayout}
                  />
                ))}
              </Grid>
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Wrong Way Section */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={wrongWayEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setWrongWayEnabled(enabled);
                  onPayoutConfigChange({
                    ...payoutConfig,
                    wrongWayEnabled: enabled,
                    quarterWrongWayPayouts: enabled ? payoutConfig.quarterWrongWayPayouts : {},
                  });
                }}
              />
            }
            label="Wrong Way Payouts"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            Wrong Way pays a bonus to the square owner whose numbers match the score in reverse (away-home instead of home-away).
          </Typography>
          <Collapse in={wrongWayEnabled}>
            <Box sx={{ ml: 4 }}>
              <Grid container spacing={2}>
                {quarterLabels.map(({ key, label }) => (
                  <Grid size={{ xs: 6, sm: 3 }} key={key}>
                    <TextField
                      fullWidth
                      label={label}
                      type="number"
                      value={payoutConfig.quarterWrongWayPayouts?.[key] || ''}
                      onChange={(e) => updateQuarterWrongWayPayout(key, parseFloat(e.target.value) || 0)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      size="small"
                      placeholder="0"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Plus N Rule Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={plusNEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setPlusNEnabled(enabled);
                    if (enabled) {
                      onPayoutConfigChange({
                        ...payoutConfig,
                        plusFiveEnabled: true,
                        plusNOffset: payoutConfig.plusNOffset ?? 5,
                        plusNRightWayEnabled: plusNRightWayEnabled,
                        plusNWrongWayEnabled: plusNWrongWayEnabled,
                      });
                    } else {
                      onPayoutConfigChange({
                        ...payoutConfig,
                        plusFiveEnabled: false,
                        quarterPlusNPayouts: {},
                        plusNRightWayEnabled: false,
                        plusNWrongWayEnabled: false,
                      });
                    }
                  }}
                />
              }
              label={`+${payoutConfig.plusNOffset ?? 5} Rule`}
            />
            {plusNEnabled && (
              <TextField
                label="Offset"
                type="number"
                value={payoutConfig.plusNOffset ?? 5}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 5;
                  const clamped = Math.max(1, Math.min(9, value));
                  onPayoutConfigChange({
                    ...payoutConfig,
                    plusNOffset: clamped,
                  });
                }}
                size="small"
                sx={{ width: 80 }}
                inputProps={{ min: 1, max: 9 }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            The +{payoutConfig.plusNOffset ?? 5} Rule pays a bonus when the winning square's numbers each differ by {payoutConfig.plusNOffset ?? 5} from the score digits
            (e.g., if score ends in 2-7, the +5 square is 7-2).
          </Typography>
          <Collapse in={plusNEnabled}>
            <Box sx={{ ml: 4 }}>
              {/* Apply to Right Way */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={plusNRightWayEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setPlusNRightWayEnabled(enabled);
                        onPayoutConfigChange({
                          ...payoutConfig,
                          plusNRightWayEnabled: enabled,
                        });
                      }}
                    />
                  }
                  label={<Typography variant="body2">Apply to Right Way</Typography>}
                />
                <Collapse in={plusNRightWayEnabled}>
                  <Box sx={{ ml: 4, mt: 1 }}>
                    <Grid container spacing={2}>
                      {quarterLabels.map(({ key, label }) => (
                        <Grid size={{ xs: 6, sm: 3 }} key={key}>
                          <TextField
                            fullWidth
                            label={label}
                            type="number"
                            value={payoutConfig.quarterPlusNPayouts?.[key] || ''}
                            onChange={(e) => updateQuarterPlusNPayout(key, parseFloat(e.target.value) || 0)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            size="small"
                            placeholder="0"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Collapse>
              </Box>

              {/* Apply to Wrong Way */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={plusNWrongWayEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setPlusNWrongWayEnabled(enabled);
                        onPayoutConfigChange({
                          ...payoutConfig,
                          plusNWrongWayEnabled: enabled,
                          quarterPlusNWrongWayPayouts: enabled ? payoutConfig.quarterPlusNWrongWayPayouts : {},
                        });
                      }}
                    />
                  }
                  label={<Typography variant="body2">Apply to Wrong Way</Typography>}
                />
                <Collapse in={plusNWrongWayEnabled}>
                  <Box sx={{ ml: 4, mt: 1 }}>
                    <Grid container spacing={2}>
                      {quarterLabels.map(({ key, label }) => (
                        <Grid size={{ xs: 6, sm: 3 }} key={key}>
                          <TextField
                            fullWidth
                            label={label}
                            type="number"
                            value={payoutConfig.quarterPlusNWrongWayPayouts?.[key] || ''}
                            onChange={(e) => updateQuarterPlusNWrongWayPayout(key, parseFloat(e.target.value) || 0)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            size="small"
                            placeholder="0"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ my: 2 }} />

        {cellMode !== 'standard' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>100-Cell Boards Only:</strong> Neighbor payouts (both regular and box) require a 100-cell board because neighbor relationships must align with individual square ownership.
          </Alert>
        )}

        {/* Neighbor Payouts Section (Full Mode) */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={neighborEnabled}
                disabled={cellMode !== 'standard'}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setNeighborEnabled(enabled);
                  if (enabled) {
                    // Disable box neighbors when enabling wraparound neighbors
                    // Use single onPayoutConfigChange call to ensure both updates happen atomically
                    if (boxNeighborEnabled) {
                      setBoxNeighborEnabled(false);
                    }
                    onPayoutConfigChange({
                      ...payoutConfig,
                      boxNeighborPayouts: {
                        ...(payoutConfig.boxNeighborPayouts || { enabled: false, boxSize: '2x2' }),
                        enabled: false,
                      } as BoxNeighborConfig,
                      neighborPayouts: {
                        ...(payoutConfig.neighborPayouts || { enabled: false }),
                        enabled: true,
                        directions: neighborConfig?.directions?.length ? neighborConfig.directions : CARDINAL_DIRECTIONS,
                      },
                    });
                  } else {
                    updateNeighborConfig({ enabled: false });
                  }
                }}
              />
            }
            label="Neighbor Payouts"
          />
          <Box sx={{ ml: 4, mb: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Neighbor payouts reward squares adjacent to the winning square. Select which directions should receive payouts.
              The grid wraps around (toroidal), so edge squares still have neighbors on the opposite side.
            </Typography>
            <Tooltip title="Learn more about neighbor payouts">
              <IconButton
                component={Link}
                to="/support/payouts#neighbor-payouts"
                target="_blank"
                rel="noopener"
                size="small"
                sx={{ p: 0.5, flexShrink: 0 }}
              >
                <HelpIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          </Box>
          <Collapse in={neighborEnabled}>
            <Box sx={{ ml: 4 }}>
              {/* Direction Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Click cells to select directions:</Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {/* 3x3 Clickable Grid */}
                  {(() => {
                    const selectedDirections = new Set(neighborConfig?.directions || []);
                    const gridLayout: (NeighborDirection | 'WIN')[][] = [
                      ['NW', 'N', 'NE'],
                      ['W', 'WIN', 'E'],
                      ['SW', 'S', 'SE'],
                    ];

                    return (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, 52px)',
                          gridTemplateRows: 'repeat(3, 52px)',
                          gap: '4px',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 0.75,
                          bgcolor: 'grey.100',
                        }}
                      >
                        {gridLayout.flat().map((cell, idx) => {
                          const isCenter = cell === 'WIN';
                          const isSelected = !isCenter && selectedDirections.has(cell);

                          return (
                            <Box
                              key={idx}
                              onClick={isCenter ? undefined : () => toggleNeighborDirection(cell)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 0.5,
                                cursor: isCenter ? 'default' : 'pointer',
                                bgcolor: isCenter
                                  ? 'success.main'
                                  : isSelected
                                    ? 'info.main'
                                    : 'grey.300',
                                color: isCenter || isSelected ? 'white' : 'text.secondary',
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                transition: 'all 0.15s ease',
                                '&:hover': isCenter ? {} : {
                                  bgcolor: isSelected ? 'info.dark' : 'grey.400',
                                  transform: 'scale(1.05)',
                                },
                                userSelect: 'none',
                              }}
                            >
                              {isCenter ? 'WIN' : cell}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  })()}

                  {/* Quick selection buttons */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">Quick select:</Typography>
                    <Button size="small" variant="outlined" onClick={setAllCardinalDirections} sx={{ justifyContent: 'flex-start' }}>
                      Cardinal (N, S, E, W)
                    </Button>
                    <Button size="small" variant="outlined" onClick={setAllDiagonalDirections} sx={{ justifyContent: 'flex-start' }}>
                      Diagonal (NE, SE, SW, NW)
                    </Button>
                    <Button size="small" variant="outlined" onClick={setAllDirections} sx={{ justifyContent: 'flex-start' }}>
                      All 8 Directions
                    </Button>
                    <Button size="small" variant="outlined" onClick={clearDirections} sx={{ justifyContent: 'flex-start' }}>
                      Clear
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* Payout Amount Configuration */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={neighborUsePerQuarter}
                      onChange={(e) => {
                        const usePerQuarter = e.target.checked;
                        setNeighborUsePerQuarter(usePerQuarter);
                        updateNeighborConfig({ usePerQuarterAmounts: usePerQuarter });
                      }}
                    />
                  }
                  label="Use per-quarter amounts"
                />
              </Box>

              {!neighborUsePerQuarter ? (
                <TextField
                  label="Amount per neighbor"
                  type="number"
                  value={neighborConfig?.globalAmount || ''}
                  onChange={(e) => updateNeighborConfig({ globalAmount: parseFloat(e.target.value) || 0 })}
                  size="small"
                  sx={{ width: 180 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  placeholder="0"
                  helperText="Each neighbor direction receives this amount"
                />
              ) : (
                <Grid container spacing={2}>
                  {quarterLabels.map(({ key, label }) => (
                    <Grid size={{ xs: 6, sm: 3 }} key={key}>
                      <TextField
                        fullWidth
                        label={label}
                        type="number"
                        value={neighborConfig?.quarterAmounts?.[key] || ''}
                        onChange={(e) => updateNeighborQuarterAmount(key, parseFloat(e.target.value) || 0)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        size="small"
                        placeholder="0"
                      />
                    </Grid>
                  ))}
                </Grid>
              )}

              {neighborAllocatedDollars > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Total neighbor allocation: ${neighborAllocatedDollars.toFixed(2)} ({neighborDirections.length} direction{neighborDirections.length !== 1 ? 's' : ''} x {quarterKeys.length} quarter{quarterKeys.length !== 1 ? 's' : ''})
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Box Neighbor Payouts Section (Full Mode) */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={boxNeighborEnabled}
                disabled={cellMode !== 'standard'}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setBoxNeighborEnabled(enabled);
                  if (enabled) {
                    // Disable wraparound neighbors when enabling box neighbors
                    // Use single onPayoutConfigChange call to ensure both updates happen atomically
                    if (neighborEnabled) {
                      setNeighborEnabled(false);
                    }
                    onPayoutConfigChange({
                      ...payoutConfig,
                      neighborPayouts: {
                        ...(payoutConfig.neighborPayouts || { enabled: false }),
                        enabled: false,
                      },
                      boxNeighborPayouts: {
                        ...(payoutConfig.boxNeighborPayouts || { enabled: false, boxSize: '2x2' }),
                        enabled: true,
                        boxSize: boxNeighborConfig?.boxSize || '2x2',
                      } as BoxNeighborConfig,
                    });
                  } else {
                    updateBoxNeighborConfig({ enabled: false });
                  }
                }}
              />
            }
            label="Box Neighbor Payouts"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
            Divides the grid into fixed rectangular groups. When a square wins, its box neighbors get paid.
            <Tooltip title="Learn more about box neighbor payouts">
              <IconButton
                component={Link}
                to="/support/payouts#box-neighbor-payouts"
                target="_blank"
                rel="noopener"
                size="small"
                sx={{ p: 0.5, ml: 0.5 }}
              >
                <HelpIcon fontSize="small" color="action" />
              </IconButton>
            </Tooltip>
          </Typography>

          {neighborEnabled && cellMode === 'standard' && (
            <Typography variant="body2" color="warning.main" sx={{ ml: 4, mt: 1 }}>
              Note: You cannot use both wraparound neighbors and box neighbors on the same board.
            </Typography>
          )}

          <Collapse in={boxNeighborEnabled}>
            <Box sx={{ ml: 4, mt: 2 }}>
              {/* Box Size Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>Box Size</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {BOX_NEIGHBOR_SIZES.map(({ value, label, neighborCount }) => (
                    <Chip
                      key={value}
                      label={`${label} (${neighborCount} neighbors)`}
                      onClick={() => updateBoxNeighborConfig({ boxSize: value })}
                      color={boxNeighborConfig?.boxSize === value ? 'primary' : 'default'}
                      variant={boxNeighborConfig?.boxSize === value ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {BOX_NEIGHBOR_SIZES.find(s => s.value === (boxNeighborConfig?.boxSize || '2x2'))?.description}
                </Typography>
              </Box>

              {/* Visual Box Preview */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>Box Layout Preview</Typography>
                {(() => {
                  const boxSize = boxNeighborConfig?.boxSize || '2x2';
                  // 2x2: 5 cols x 5 rows of 4-square boxes
                  // 5x2: 2 cols x 5 rows of 10-square boxes
                  // 5x5: 2 cols x 2 rows of 25-square quadrants
                  const boxWidth = boxSize === '2x2' ? 2 : 5;
                  const boxHeight = boxSize === '5x5' ? 5 : 2;
                  const colGroups = 10 / boxWidth;
                  const rowGroups = 10 / boxHeight;
                  const squaresPerBox = boxWidth * boxHeight;

                  return (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${colGroups}, 1fr)`,
                        gridTemplateRows: `repeat(${rowGroups}, 1fr)`,
                        gap: '3px',
                        width: boxSize === '2x2' ? 180 : boxSize === '5x5' ? 180 : 250,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 0.75,
                        bgcolor: 'grey.100',
                      }}
                    >
                      {Array.from({ length: rowGroups * colGroups }).map((_, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            aspectRatio: boxSize === '2x2' ? '1' : boxSize === '5x5' ? '1' : '2.5',
                            bgcolor: idx % 2 === (Math.floor(idx / colGroups) % 2) ? 'info.light' : 'info.main',
                            borderRadius: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {squaresPerBox} sq
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>

              {/* Payout Amount Configuration */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={boxNeighborUsePerQuarter}
                      onChange={(e) => {
                        const usePerQuarter = e.target.checked;
                        setBoxNeighborUsePerQuarter(usePerQuarter);
                        updateBoxNeighborConfig({ usePerQuarterAmounts: usePerQuarter });
                      }}
                    />
                  }
                  label="Use per-quarter amounts"
                />
              </Box>

              {!boxNeighborUsePerQuarter ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="body2">Amount per neighbor:</Typography>
                  <TextField
                    type="number"
                    value={boxNeighborConfig?.globalAmount || ''}
                    onChange={(e) => updateBoxNeighborConfig({ globalAmount: parseFloat(e.target.value) || 0 })}
                    size="small"
                    sx={{ width: 120 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    placeholder="0"
                  />
                  <Typography variant="body2" color="text.secondary">
                    (Each neighbor gets the full amount)
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {quarterLabels.map(({ key, label }) => (
                    <Grid size={{ xs: 6, sm: 3 }} key={key}>
                      <TextField
                        fullWidth
                        label={label}
                        type="number"
                        value={boxNeighborConfig?.quarterAmounts?.[key] || ''}
                        onChange={(e) => updateBoxNeighborQuarterAmount(key, parseFloat(e.target.value) || 0)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        size="small"
                        placeholder="0"
                      />
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Total calculation */}
              {(() => {
                const neighborCount = getBoxNeighborCount(boxNeighborConfig?.boxSize || '2x2');
                let amountSumAcrossQuarters: number;
                if (boxNeighborUsePerQuarter && boxNeighborConfig?.quarterAmounts) {
                  // Sum of all per-quarter amounts
                  amountSumAcrossQuarters = Object.values(boxNeighborConfig.quarterAmounts).reduce((sum, amt) => sum + (amt || 0), 0);
                } else {
                  // Global amount × number of quarters
                  amountSumAcrossQuarters = (boxNeighborConfig?.globalAmount || 0) * quarterLabels.length;
                }
                const totalAllocation = amountSumAcrossQuarters * neighborCount;

                return totalAllocation > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Total box neighbor allocation: ${totalAllocation.toFixed(2)} ({neighborCount} neighbors x {quarterLabels.length} quarters)
                  </Typography>
                ) : null;
              })()}
            </Box>
          </Collapse>
        </Box>

        {/* Additional Payout Options */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Overtime Configuration - only show for 4 quarters */}
          {quarters === 4 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={payoutConfig.overtimeConfig?.enabled ?? false}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        if (enabled) {
                          onPayoutConfigChange({
                            ...payoutConfig,
                            overtimeConfig: {
                              enabled: true,
                              overtimeAmount: payoutConfig.overtimeConfig?.overtimeAmount ?? 0,
                            },
                          });
                        } else {
                          // Clear overtime amount when disabled
                          onPayoutConfigChange({
                            ...payoutConfig,
                            overtimeConfig: {
                              enabled: false,
                              overtimeAmount: 0,
                            },
                          });
                        }
                      }}
                    />
                  }
                  label="Enable OT split"
                />
                {payoutConfig.overtimeConfig?.enabled && (
                  <TextField
                    label="Final (OT) payout"
                    value={otAmountInput.inputValue}
                    onChange={otAmountInput.handleChange}
                    onBlur={otAmountInput.handleBlur}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    helperText={`${lastQuarterKey}: $${regulationPayout.toFixed(2)}, Final (OT): $${otAmount.toFixed(2)}`}
                    size="small"
                    sx={{ width: 200 }}
                  />
                )}
              </Box>
              <Collapse in={payoutConfig.overtimeConfig?.enabled}>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                  If overtime occurs, the {lastQuarterLabel.split(' /')[0]} payout ($
                  {q4Payout.toFixed(2)}) is split: ${regulationPayout.toFixed(2)} for end of regulation,
                  ${otAmount.toFixed(2)} for final OT score.
                </Typography>
              </Collapse>
            </Box>
          )}
        </Box>

        {/* Bad Numbers Section */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={badNumbersEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setBadNumbersEnabled(enabled);
                    if (enabled) {
                      // Default to 2s only preset when enabling (if no combos already selected)
                      const defaultCombos = selectedBadCombos.length > 0 ? selectedBadCombos : getDigitPresetCombos(2);
                      setSelectedBadCombos(defaultCombos);
                      updateBadNumbersConfig({
                        enabled: true,
                        badCombinations: defaultCombos,
                        payoutAmount: badNumbersPayoutAmount || 10,
                      });
                    } else {
                      updateBadNumbersConfig({ enabled: false });
                    }
                  }}
                />
              }
              label="Bad Numbers"
            />
            {badNumbersEnabled && badNumbersAllocatedDollars > 0 && (
              <Typography variant="body2" color="text.secondary">
                {selectedBadCombos.length} squares, ${badNumbersAllocatedDollars.toFixed(2)} total
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
            Compensate players with "unlucky" number combinations (right-way only).
            Payouts are credited automatically when the board locks.
          </Typography>
          <Collapse in={badNumbersEnabled}>
            <Box sx={{ ml: 4, mt: 2 }}>
              {/* Preset selector */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="2,5,8,9"
                    variant={isAllPresetActive ? 'filled' : 'outlined'}
                    color={isAllPresetActive ? 'primary' : 'default'}
                    onClick={() => {
                      setSelectedBadCombos([...ALL_BAD_COMBOS]);
                      updateBadNumbersConfig({ badCombinations: [...ALL_BAD_COMBOS] });
                    }}
                    size="small"
                  />
                  {DIGIT_PRESETS.map(({ digit, label }) => {
                    const combos = getDigitPresetCombos(digit);
                    return (
                      <Chip
                        key={digit}
                        label={label}
                        variant={isDigitPresetActive(digit) ? 'filled' : 'outlined'}
                        color={isDigitPresetActive(digit) ? 'primary' : 'default'}
                        onClick={() => {
                          setSelectedBadCombos([...combos]);
                          updateBadNumbersConfig({ badCombinations: [...combos] });
                        }}
                        size="small"
                      />
                    );
                  })}
                  <Chip
                    label="Clear All"
                    variant="outlined"
                    onClick={() => {
                      setSelectedBadCombos([]);
                      updateBadNumbersConfig({ badCombinations: [] });
                    }}
                    size="small"
                  />
                </Box>
              </Box>

              {/* Compact 10x10 Grid Selector */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Click cells to toggle bad combinations:
                </Typography>
                <Box sx={{
                  display: 'inline-grid',
                  gridTemplateColumns: 'auto repeat(10, 24px)',
                  gap: '2px',
                  bgcolor: 'grey.100',
                  p: 0.5,
                  borderRadius: 1,
                }}>
                  {/* Header row */}
                  <Box /> {/* Empty corner */}
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(col => (
                    <Box
                      key={`header-${col}`}
                      sx={{
                        width: 24,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.65rem',
                        fontWeight: 'bold',
                        color: 'text.secondary',
                      }}
                    >
                      {col}
                    </Box>
                  ))}

                  {/* Grid rows */}
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(row => (
                    <React.Fragment key={`row-${row}`}>
                      <Box
                        sx={{
                          width: 16,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.65rem',
                          fontWeight: 'bold',
                          color: 'text.secondary',
                        }}
                      >
                        {row}
                      </Box>
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(col => {
                        const combo = `${row}-${col}`;
                        const isSelected = selectedBadCombos.includes(combo);
                        return (
                          <Box
                            key={combo}
                            onClick={() => toggleBadCombo(combo)}
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: isSelected ? 'error.main' : 'background.paper',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.1s',
                              '&:hover': {
                                bgcolor: isSelected ? 'error.dark' : 'grey.300',
                              },
                            }}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>

              {/* Payout amount */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="body2">
                  Payout per square:
                </Typography>
                <TextField
                  type="number"
                  value={badNumbersPayoutAmount || ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setBadNumbersPayoutAmount(value);
                    updateBadNumbersConfig({ payoutAmount: value });
                  }}
                  size="small"
                  sx={{ width: 120 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  placeholder="10"
                />
              </Box>

              {/* Summary */}
              {selectedBadCombos.length > 0 && badNumbersPayoutAmount > 0 && (
                <Box sx={{ p: 1.5, bgcolor: 'warning.light', borderRadius: 1, mt: 2 }}>
                  <Typography variant="body2">
                    <strong>{selectedBadCombos.length}</strong> bad squares
                    {' × '}
                    <strong>${badNumbersPayoutAmount.toFixed(2)}</strong>
                    {' = '}
                    <strong>${(selectedBadCombos.length * badNumbersPayoutAmount).toFixed(2)}</strong> total
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Custom Payout Categories */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">
              Custom Payouts
            </Typography>
            {(payoutConfig.customCategories?.length || 0) > 0 && (
              <Typography variant="body2" color="text.secondary">
                ({payoutConfig.customCategories?.length} categories, ${customAllocatedDollars.toFixed(2)})
              </Typography>
            )}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add custom stat-based payouts (e.g., Passing Yards, Rush Yards). When Wrong Way is enabled for a category, it automatically pays both the right-way and wrong-way winners, doubling the payout.
        </Typography>
        {(payoutConfig.customCategories || []).map((category) => (
          <Box key={category.id} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', minWidth: 0, flexWrap: 'wrap' }}>
              <TextField
                label="Name"
                value={category.name}
                onChange={(e) => updateCustomCategory(category.id, { name: e.target.value })}
                size="small"
                sx={{ flex: 1, minWidth: 150 }}
                placeholder="e.g., Passing Yards"
              />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexShrink: 0 }}>
                <TextField
                  label="Amount"
                  type="number"
                  value={category.payoutAmount}
                  onChange={(e) => updateCustomCategory(category.id, { payoutAmount: parseFloat(e.target.value) || 0 })}
                  size="small"
                  sx={{ width: 140 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                <IconButton size="small" onClick={() => removeCustomCategory(category.id)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={category.wrongWayEnabled || false}
                    onChange={(e) => updateCustomCategory(category.id, { wrongWayEnabled: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="caption">
                    Wrong Way {category.wrongWayEnabled && category.payoutAmount > 0 && (
                      <Typography component="span" variant="caption" color="success.main">
                        (${category.payoutAmount.toFixed(2)} × 2 = ${(category.payoutAmount * 2).toFixed(2)} total)
                      </Typography>
                    )}
                  </Typography>
                }
              />
            </Box>
          </Box>
        ))}
        <Button startIcon={<AddIcon />} onClick={addCustomCategory} size="small" variant="outlined">
          Add Custom Payout
        </Button>
      </Paper>
    </Box>
  );
};
